import { distinctUntilChanged, filter, shareReplay, switchMap, map } from 'rxjs/operators';
import { Injectable, InjectionToken, APP_INITIALIZER, NgModule, Optional, Inject, PLATFORM_ID, NgZone, defineInjectable, inject } from '@angular/core';
import { of, Subject } from 'rxjs';
import { __extends, __spread } from 'tslib';
import { isPlatformBrowser } from '@angular/common';

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/** @enum {string} */
var StorageStrategies = {
    Local: 'local_strategy',
    Session: 'session_strategy',
    InMemory: 'in_memory_strategy',
};

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * @return {?}
 */
function noop() { }

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/** @type {?} */
var DefaultPrefix = 'ngx-webstorage';
/** @type {?} */
var DefaultSeparator = '|';
/** @type {?} */
var DefaultIsCaseSensitive = false;

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var StorageKeyManager = /** @class */ (function () {
    function StorageKeyManager() {
    }
    /**
     * @param {?} raw
     * @return {?}
     */
    StorageKeyManager.normalize = /**
     * @param {?} raw
     * @return {?}
     */
    function (raw) {
        raw = StorageKeyManager.isCaseSensitive ? raw : raw.toLowerCase();
        return "" + StorageKeyManager.prefix + StorageKeyManager.separator + raw;
    };
    /**
     * @param {?} key
     * @return {?}
     */
    StorageKeyManager.isNormalizedKey = /**
     * @param {?} key
     * @return {?}
     */
    function (key) {
        return key.indexOf(StorageKeyManager.prefix + StorageKeyManager.separator) === 0;
    };
    /**
     * @param {?} prefix
     * @return {?}
     */
    StorageKeyManager.setPrefix = /**
     * @param {?} prefix
     * @return {?}
     */
    function (prefix) {
        StorageKeyManager.prefix = prefix;
    };
    /**
     * @param {?} separator
     * @return {?}
     */
    StorageKeyManager.setSeparator = /**
     * @param {?} separator
     * @return {?}
     */
    function (separator) {
        StorageKeyManager.separator = separator;
    };
    /**
     * @param {?} enable
     * @return {?}
     */
    StorageKeyManager.setCaseSensitive = /**
     * @param {?} enable
     * @return {?}
     */
    function (enable) {
        StorageKeyManager.isCaseSensitive = enable;
    };
    /**
     * @param {?} config
     * @return {?}
     */
    StorageKeyManager.consumeConfiguration = /**
     * @param {?} config
     * @return {?}
     */
    function (config) {
        if ('prefix' in config)
            this.setPrefix(config.prefix);
        if ('separator' in config)
            this.setSeparator(config.separator);
        if ('caseSensitive' in config)
            this.setCaseSensitive(config.caseSensitive);
    };
    StorageKeyManager.prefix = DefaultPrefix;
    StorageKeyManager.separator = DefaultSeparator;
    StorageKeyManager.isCaseSensitive = DefaultIsCaseSensitive;
    return StorageKeyManager;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var SyncStorage = /** @class */ (function () {
    function SyncStorage(strategy) {
        this.strategy = strategy;
    }
    /**
     * @param {?} key
     * @return {?}
     */
    SyncStorage.prototype.retrieve = /**
     * @param {?} key
     * @return {?}
     */
    function (key) {
        /** @type {?} */
        var value;
        this.strategy.get(StorageKeyManager.normalize(key)).subscribe(function (result) { return value = typeof result === 'undefined' ? null : result; });
        return value;
    };
    /**
     * @param {?} key
     * @param {?} value
     * @return {?}
     */
    SyncStorage.prototype.store = /**
     * @param {?} key
     * @param {?} value
     * @return {?}
     */
    function (key, value) {
        this.strategy.set(StorageKeyManager.normalize(key), value).subscribe(noop);
        return value;
    };
    /**
     * @param {?=} key
     * @return {?}
     */
    SyncStorage.prototype.clear = /**
     * @param {?=} key
     * @return {?}
     */
    function (key) {
        if (key !== undefined)
            this.strategy.del(StorageKeyManager.normalize(key)).subscribe(noop);
        else
            this.strategy.clear().subscribe(noop);
    };
    /**
     * @return {?}
     */
    SyncStorage.prototype.getStrategyName = /**
     * @return {?}
     */
    function () { return this.strategy.name; };
    /**
     * @param {?} key
     * @return {?}
     */
    SyncStorage.prototype.observe = /**
     * @param {?} key
     * @return {?}
     */
    function (key) {
        var _this = this;
        key = StorageKeyManager.normalize(key);
        return this.strategy.keyChanges.pipe(filter(function (changed) { return changed === null || changed === key; }), switchMap(function () { return _this.strategy.get(key); }), distinctUntilChanged(), shareReplay());
    };
    return SyncStorage;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var AsyncStorage = /** @class */ (function () {
    function AsyncStorage(strategy) {
        this.strategy = strategy;
    }
    /**
     * @param {?} key
     * @return {?}
     */
    AsyncStorage.prototype.retrieve = /**
     * @param {?} key
     * @return {?}
     */
    function (key) {
        return this.strategy.get(StorageKeyManager.normalize(key)).pipe(map(function (value) { return typeof value === 'undefined' ? null : value; }));
    };
    /**
     * @param {?} key
     * @param {?} value
     * @return {?}
     */
    AsyncStorage.prototype.store = /**
     * @param {?} key
     * @param {?} value
     * @return {?}
     */
    function (key, value) {
        return this.strategy.set(StorageKeyManager.normalize(key), value);
    };
    /**
     * @param {?=} key
     * @return {?}
     */
    AsyncStorage.prototype.clear = /**
     * @param {?=} key
     * @return {?}
     */
    function (key) {
        return key !== undefined ? this.strategy.del(StorageKeyManager.normalize(key)) : this.strategy.clear();
    };
    /**
     * @return {?}
     */
    AsyncStorage.prototype.getStrategyName = /**
     * @return {?}
     */
    function () { return this.strategy.name; };
    /**
     * @param {?} key
     * @return {?}
     */
    AsyncStorage.prototype.observe = /**
     * @param {?} key
     * @return {?}
     */
    function (key) {
        var _this = this;
        key = StorageKeyManager.normalize(key);
        return this.strategy.keyChanges.pipe(filter(function (changed) { return changed === null || changed === key; }), switchMap(function () { return _this.strategy.get(key); }), distinctUntilChanged(), shareReplay());
    };
    return AsyncStorage;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var StrategyCacheService = /** @class */ (function () {
    function StrategyCacheService() {
        this.caches = {};
    }
    /**
     * @param {?} strategyName
     * @param {?} key
     * @return {?}
     */
    StrategyCacheService.prototype.get = /**
     * @param {?} strategyName
     * @param {?} key
     * @return {?}
     */
    function (strategyName, key) {
        return this.getCacheStore(strategyName)[key];
    };
    /**
     * @param {?} strategyName
     * @param {?} key
     * @param {?} value
     * @return {?}
     */
    StrategyCacheService.prototype.set = /**
     * @param {?} strategyName
     * @param {?} key
     * @param {?} value
     * @return {?}
     */
    function (strategyName, key, value) {
        this.getCacheStore(strategyName)[key] = value;
    };
    /**
     * @param {?} strategyName
     * @param {?} key
     * @return {?}
     */
    StrategyCacheService.prototype.del = /**
     * @param {?} strategyName
     * @param {?} key
     * @return {?}
     */
    function (strategyName, key) {
        delete this.getCacheStore(strategyName)[key];
    };
    /**
     * @param {?} strategyName
     * @return {?}
     */
    StrategyCacheService.prototype.clear = /**
     * @param {?} strategyName
     * @return {?}
     */
    function (strategyName) {
        this.caches[strategyName] = (/** @type {?} */ ({}));
    };
    /**
     * @param {?} strategyName
     * @return {?}
     */
    StrategyCacheService.prototype.getCacheStore = /**
     * @param {?} strategyName
     * @return {?}
     */
    function (strategyName) {
        if (strategyName in this.caches)
            return this.caches[strategyName];
        return this.caches[strategyName] = (/** @type {?} */ ({}));
    };
    StrategyCacheService.decorators = [
        { type: Injectable, args: [{ providedIn: 'root' },] }
    ];
    /** @nocollapse */ StrategyCacheService.ngInjectableDef = defineInjectable({ factory: function StrategyCacheService_Factory() { return new StrategyCacheService(); }, token: StrategyCacheService, providedIn: "root" });
    return StrategyCacheService;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/** @type {?} */
var LOCAL_STORAGE = new InjectionToken('window_local_storage');
/**
 * @return {?}
 */
function getLocalStorage() {
    return (typeof window !== 'undefined') ? window.localStorage : null;
}
/** @type {?} */
var LocalStorageProvider = { provide: LOCAL_STORAGE, useFactory: getLocalStorage };
/** @type {?} */
var SESSION_STORAGE = new InjectionToken('window_session_storage');
/**
 * @return {?}
 */
function getSessionStorage() {
    return (typeof window !== 'undefined') ? window.sessionStorage : null;
}
/** @type {?} */
var SessionStorageProvider = { provide: SESSION_STORAGE, useFactory: getSessionStorage };

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var CompatHelper = /** @class */ (function () {
    function CompatHelper() {
    }
    /**
     * @param {?} storage
     * @return {?}
     */
    CompatHelper.isStorageAvailable = /**
     * @param {?} storage
     * @return {?}
     */
    function (storage) {
        /** @type {?} */
        var available = true;
        try {
            if (typeof storage === 'object') {
                storage.setItem('test-storage', 'foobar');
                storage.removeItem('test-storage');
            }
            else
                available = false;
        }
        catch (e) {
            available = false;
        }
        return available;
    };
    return CompatHelper;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * @abstract
 */
var  /**
 * @abstract
 */
BaseSyncStorageStrategy = /** @class */ (function () {
    function BaseSyncStorageStrategy(storage, cache) {
        this.storage = storage;
        this.cache = cache;
        this.keyChanges = new Subject();
    }
    Object.defineProperty(BaseSyncStorageStrategy.prototype, "isAvailable", {
        get: /**
         * @return {?}
         */
        function () {
            if (this._isAvailable === undefined)
                this._isAvailable = CompatHelper.isStorageAvailable(this.storage);
            return this._isAvailable;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * @param {?} key
     * @return {?}
     */
    BaseSyncStorageStrategy.prototype.get = /**
     * @param {?} key
     * @return {?}
     */
    function (key) {
        /** @type {?} */
        var data = this.cache.get(this.name, key);
        if (data !== undefined)
            return of(data);
        try {
            /** @type {?} */
            var item = this.storage.getItem(key);
            if (item !== null) {
                data = JSON.parse(item);
                this.cache.set(this.name, key, data);
            }
        }
        catch (err) {
            console.warn(err);
        }
        return of(data);
    };
    /**
     * @param {?} key
     * @param {?} value
     * @return {?}
     */
    BaseSyncStorageStrategy.prototype.set = /**
     * @param {?} key
     * @param {?} value
     * @return {?}
     */
    function (key, value) {
        /** @type {?} */
        var data = JSON.stringify(value);
        this.storage.setItem(key, data);
        this.cache.set(this.name, key, value);
        this.keyChanges.next(key);
        return of(value);
    };
    /**
     * @param {?} key
     * @return {?}
     */
    BaseSyncStorageStrategy.prototype.del = /**
     * @param {?} key
     * @return {?}
     */
    function (key) {
        this.storage.removeItem(key);
        this.cache.del(this.name, key);
        this.keyChanges.next(key);
        return of(null);
    };
    /**
     * @return {?}
     */
    BaseSyncStorageStrategy.prototype.clear = /**
     * @return {?}
     */
    function () {
        this.storage.clear();
        this.cache.clear(this.name);
        this.keyChanges.next(null);
        return of(null);
    };
    return BaseSyncStorageStrategy;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var LocalStorageStrategy = /** @class */ (function (_super) {
    __extends(LocalStorageStrategy, _super);
    function LocalStorageStrategy(storage, cache, platformId, zone) {
        var _this = _super.call(this, storage, cache) || this;
        _this.storage = storage;
        _this.cache = cache;
        _this.platformId = platformId;
        _this.zone = zone;
        _this.name = LocalStorageStrategy.strategyName;
        if (isPlatformBrowser(_this.platformId))
            _this.listenExternalChanges();
        return _this;
    }
    /**
     * @return {?}
     */
    LocalStorageStrategy.prototype.listenExternalChanges = /**
     * @return {?}
     */
    function () {
        var _this = this;
        window.addEventListener('storage', function (event) { return _this.zone.run(function () {
            if (event.storageArea !== _this.storage)
                return;
            /** @type {?} */
            var key = event.key;
            if (key !== null)
                _this.cache.del(_this.name, event.key);
            else
                _this.cache.clear(_this.name);
            _this.keyChanges.next(key);
        }); });
    };
    LocalStorageStrategy.strategyName = StorageStrategies.Local;
    /** @nocollapse */
    LocalStorageStrategy.ctorParameters = function () { return [
        { type: undefined, decorators: [{ type: Inject, args: [LOCAL_STORAGE,] }] },
        { type: StrategyCacheService },
        { type: undefined, decorators: [{ type: Inject, args: [PLATFORM_ID,] }] },
        { type: NgZone }
    ]; };
    return LocalStorageStrategy;
}(BaseSyncStorageStrategy));

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var SessionStorageStrategy = /** @class */ (function (_super) {
    __extends(SessionStorageStrategy, _super);
    function SessionStorageStrategy(storage, cache, platformId, zone) {
        var _this = _super.call(this, storage, cache) || this;
        _this.storage = storage;
        _this.cache = cache;
        _this.platformId = platformId;
        _this.zone = zone;
        _this.name = SessionStorageStrategy.strategyName;
        if (isPlatformBrowser(_this.platformId))
            _this.listenExternalChanges();
        return _this;
    }
    /**
     * @return {?}
     */
    SessionStorageStrategy.prototype.listenExternalChanges = /**
     * @return {?}
     */
    function () {
        var _this = this;
        window.addEventListener('storage', function (event) { return _this.zone.run(function () {
            if (event.storageArea !== _this.storage)
                return;
            /** @type {?} */
            var key = event.key;
            if (event.key !== null)
                _this.cache.del(_this.name, event.key);
            else
                _this.cache.clear(_this.name);
            _this.keyChanges.next(key);
        }); });
    };
    SessionStorageStrategy.strategyName = StorageStrategies.Session;
    /** @nocollapse */
    SessionStorageStrategy.ctorParameters = function () { return [
        { type: undefined, decorators: [{ type: Inject, args: [SESSION_STORAGE,] }] },
        { type: StrategyCacheService },
        { type: undefined, decorators: [{ type: Inject, args: [PLATFORM_ID,] }] },
        { type: NgZone }
    ]; };
    return SessionStorageStrategy;
}(BaseSyncStorageStrategy));

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var InMemoryStorageStrategy = /** @class */ (function () {
    function InMemoryStorageStrategy(cache) {
        this.cache = cache;
        this.keyChanges = new Subject();
        this.isAvailable = true;
        this.name = InMemoryStorageStrategy.strategyName;
    }
    /**
     * @param {?} key
     * @return {?}
     */
    InMemoryStorageStrategy.prototype.get = /**
     * @param {?} key
     * @return {?}
     */
    function (key) {
        return of(this.cache.get(this.name, key));
    };
    /**
     * @param {?} key
     * @param {?} value
     * @return {?}
     */
    InMemoryStorageStrategy.prototype.set = /**
     * @param {?} key
     * @param {?} value
     * @return {?}
     */
    function (key, value) {
        this.cache.set(this.name, key, value);
        this.keyChanges.next(key);
        return of(value);
    };
    /**
     * @param {?} key
     * @return {?}
     */
    InMemoryStorageStrategy.prototype.del = /**
     * @param {?} key
     * @return {?}
     */
    function (key) {
        this.cache.del(this.name, key);
        this.keyChanges.next(key);
        return of(null);
    };
    /**
     * @return {?}
     */
    InMemoryStorageStrategy.prototype.clear = /**
     * @return {?}
     */
    function () {
        this.cache.clear(this.name);
        this.keyChanges.next(null);
        return of(null);
    };
    InMemoryStorageStrategy.strategyName = StorageStrategies.InMemory;
    /** @nocollapse */
    InMemoryStorageStrategy.ctorParameters = function () { return [
        { type: StrategyCacheService, decorators: [{ type: Inject, args: [StrategyCacheService,] }] }
    ]; };
    return InMemoryStorageStrategy;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/** @type {?} */
var STORAGE_STRATEGIES = new InjectionToken('STORAGE_STRATEGIES');
/** @type {?} */
var Strategies = [
    { provide: STORAGE_STRATEGIES, useClass: InMemoryStorageStrategy, multi: true },
    { provide: STORAGE_STRATEGIES, useClass: LocalStorageStrategy, multi: true },
    { provide: STORAGE_STRATEGIES, useClass: SessionStorageStrategy, multi: true },
];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/** @type {?} */
var StorageStrategyStubName = 'stub_strategy';
var StorageStrategyStub = /** @class */ (function () {
    function StorageStrategyStub(name) {
        this.keyChanges = new Subject();
        this.store = {};
        this._available = true;
        this.name = name || StorageStrategyStubName;
    }
    Object.defineProperty(StorageStrategyStub.prototype, "isAvailable", {
        get: /**
         * @return {?}
         */
        function () {
            return this._available;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * @param {?} key
     * @return {?}
     */
    StorageStrategyStub.prototype.get = /**
     * @param {?} key
     * @return {?}
     */
    function (key) {
        return of(this.store[key]);
    };
    /**
     * @param {?} key
     * @param {?} value
     * @return {?}
     */
    StorageStrategyStub.prototype.set = /**
     * @param {?} key
     * @param {?} value
     * @return {?}
     */
    function (key, value) {
        this.store[key] = value;
        this.keyChanges.next(key);
        return of(value);
    };
    /**
     * @param {?} key
     * @return {?}
     */
    StorageStrategyStub.prototype.del = /**
     * @param {?} key
     * @return {?}
     */
    function (key) {
        delete this.store[key];
        this.keyChanges.next(key);
        return of(null);
    };
    /**
     * @return {?}
     */
    StorageStrategyStub.prototype.clear = /**
     * @return {?}
     */
    function () {
        this.store = {};
        this.keyChanges.next(null);
        return of(null);
    };
    /** @nocollapse */
    StorageStrategyStub.ctorParameters = function () { return [
        { type: String, decorators: [{ type: Optional }] }
    ]; };
    return StorageStrategyStub;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var StorageStub = /** @class */ (function () {
    function StorageStub() {
        this.store = {};
    }
    Object.defineProperty(StorageStub.prototype, "length", {
        get: /**
         * @return {?}
         */
        function () {
            return Object.keys(this.store).length;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * @return {?}
     */
    StorageStub.prototype.clear = /**
     * @return {?}
     */
    function () {
        this.store = {};
    };
    /**
     * @param {?} key
     * @return {?}
     */
    StorageStub.prototype.getItem = /**
     * @param {?} key
     * @return {?}
     */
    function (key) {
        return this.store[key] || null;
    };
    /**
     * @param {?} index
     * @return {?}
     */
    StorageStub.prototype.key = /**
     * @param {?} index
     * @return {?}
     */
    function (index) {
        return Object.keys(this.store)[index];
    };
    /**
     * @param {?} key
     * @return {?}
     */
    StorageStub.prototype.removeItem = /**
     * @param {?} key
     * @return {?}
     */
    function (key) {
        delete this.store[key];
    };
    /**
     * @param {?} key
     * @param {?} value
     * @return {?}
     */
    StorageStub.prototype.setItem = /**
     * @param {?} key
     * @param {?} value
     * @return {?}
     */
    function (key, value) {
        this.store[key] = value;
    };
    return StorageStub;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/** @type {?} */
var InvalidStrategyError = 'invalid_strategy';
var StrategyIndex = /** @class */ (function () {
    function StrategyIndex(strategies) {
        this.strategies = strategies;
        this.registration$ = new Subject();
        if (!strategies)
            strategies = [];
        this.strategies = strategies.reverse()
            .map(function (strategy, index, arr) { return strategy.name; })
            .map(function (name, index, arr) { return arr.indexOf(name) === index ? index : null; })
            .filter(function (index) { return index !== null; })
            .map(function (index) { return strategies[index]; });
    }
    /**
     * @param {?} name
     * @return {?}
     */
    StrategyIndex.get = /**
     * @param {?} name
     * @return {?}
     */
    function (name) {
        if (!this.isStrategyRegistered(name))
            throw Error(InvalidStrategyError);
        /** @type {?} */
        var strategy = StrategyIndex.index[name];
        if (!strategy.isAvailable) {
            strategy = StrategyIndex.index[StorageStrategies.InMemory];
        }
        return strategy;
    };
    /**
     * @param {?} name
     * @param {?} strategy
     * @return {?}
     */
    StrategyIndex.set = /**
     * @param {?} name
     * @param {?} strategy
     * @return {?}
     */
    function (name, strategy) {
        StrategyIndex.index[name] = strategy;
    };
    /**
     * @param {?=} name
     * @return {?}
     */
    StrategyIndex.clear = /**
     * @param {?=} name
     * @return {?}
     */
    function (name) {
        if (name !== undefined)
            delete StrategyIndex.index[name];
        else
            StrategyIndex.index = {};
    };
    /**
     * @param {?} name
     * @return {?}
     */
    StrategyIndex.isStrategyRegistered = /**
     * @param {?} name
     * @return {?}
     */
    function (name) {
        return name in StrategyIndex.index;
    };
    /**
     * @return {?}
     */
    StrategyIndex.hasRegistredStrategies = /**
     * @return {?}
     */
    function () {
        return Object.keys(StrategyIndex.index).length > 0;
    };
    /**
     * @param {?} name
     * @return {?}
     */
    StrategyIndex.prototype.getStrategy = /**
     * @param {?} name
     * @return {?}
     */
    function (name) {
        return StrategyIndex.get(name);
    };
    /**
     * @return {?}
     */
    StrategyIndex.prototype.indexStrategies = /**
     * @return {?}
     */
    function () {
        var _this = this;
        this.strategies.forEach(function (strategy) { return _this.register(strategy.name, strategy); });
    };
    /**
     * @param {?} name
     * @param {?=} overrideIfExists
     * @return {?}
     */
    StrategyIndex.prototype.indexStrategy = /**
     * @param {?} name
     * @param {?=} overrideIfExists
     * @return {?}
     */
    function (name, overrideIfExists) {
        if (overrideIfExists === void 0) { overrideIfExists = false; }
        if (StrategyIndex.isStrategyRegistered(name) && !overrideIfExists)
            return StrategyIndex.get(name);
        /** @type {?} */
        var strategy = this.strategies.find(function (strategy) { return strategy.name === name; });
        if (!strategy)
            throw new Error(InvalidStrategyError);
        this.register(name, strategy, overrideIfExists);
        return strategy;
    };
    /**
     * @param {?} name
     * @param {?} strategy
     * @param {?=} overrideIfExists
     * @return {?}
     */
    StrategyIndex.prototype.register = /**
     * @param {?} name
     * @param {?} strategy
     * @param {?=} overrideIfExists
     * @return {?}
     */
    function (name, strategy, overrideIfExists) {
        if (overrideIfExists === void 0) { overrideIfExists = false; }
        if (!StrategyIndex.isStrategyRegistered(name) || overrideIfExists) {
            StrategyIndex.set(name, strategy);
            this.registration$.next(name);
        }
    };
    StrategyIndex.index = {};
    StrategyIndex.decorators = [
        { type: Injectable, args: [{ providedIn: 'root' },] }
    ];
    /** @nocollapse */
    StrategyIndex.ctorParameters = function () { return [
        { type: Array, decorators: [{ type: Optional }, { type: Inject, args: [STORAGE_STRATEGIES,] }] }
    ]; };
    /** @nocollapse */ StrategyIndex.ngInjectableDef = defineInjectable({ factory: function StrategyIndex_Factory() { return new StrategyIndex(inject(STORAGE_STRATEGIES, 8)); }, token: StrategyIndex, providedIn: "root" });
    return StrategyIndex;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var LocalStorageService = /** @class */ (function (_super) {
    __extends(LocalStorageService, _super);
    function LocalStorageService() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    return LocalStorageService;
}(SyncStorage));
/**
 * @param {?} index
 * @return {?}
 */
function buildService(index) {
    /** @type {?} */
    var strategy = index.indexStrategy(StorageStrategies.Local);
    return new SyncStorage(strategy);
}
/** @type {?} */
var LocalStorageServiceProvider = {
    provide: LocalStorageService,
    useFactory: buildService,
    deps: [StrategyIndex]
};

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var SessionStorageService = /** @class */ (function (_super) {
    __extends(SessionStorageService, _super);
    function SessionStorageService() {
        return _super !== null && _super.apply(this, arguments) || this;
    }
    return SessionStorageService;
}(SyncStorage));
/**
 * @param {?} index
 * @return {?}
 */
function buildService$1(index) {
    /** @type {?} */
    var strategy = index.indexStrategy(StorageStrategies.Session);
    return new SyncStorage(strategy);
}
/** @type {?} */
var SessionStorageServiceProvider = {
    provide: SessionStorageService,
    useFactory: buildService$1,
    deps: [StrategyIndex]
};

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var DecoratorBuilder = /** @class */ (function () {
    function DecoratorBuilder() {
    }
    /**
     * @param {?} strategyName
     * @param {?} prototype
     * @param {?} propName
     * @param {?=} key
     * @param {?=} defaultValue
     * @return {?}
     */
    DecoratorBuilder.buildSyncStrategyDecorator = /**
     * @param {?} strategyName
     * @param {?} prototype
     * @param {?} propName
     * @param {?=} key
     * @param {?=} defaultValue
     * @return {?}
     */
    function (strategyName, prototype, propName, key, defaultValue) {
        if (defaultValue === void 0) { defaultValue = null; }
        /** @type {?} */
        var rawKey = key || propName;
        /** @type {?} */
        var storageKey;
        Object.defineProperty(prototype, propName, {
            get: function () {
                /** @type {?} */
                var value;
                StrategyIndex.get(strategyName).get(getKey()).subscribe(function (result) { return value = result; });
                return value === undefined ? defaultValue : value;
            },
            set: function (value) {
                StrategyIndex.get(strategyName).set(getKey(), value).subscribe(noop);
            }
        });
        /**
         * @return {?}
         */
        function getKey() {
            if (storageKey !== undefined)
                return storageKey;
            return storageKey = StorageKeyManager.normalize(rawKey);
        }
    };
    return DecoratorBuilder;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * @param {?=} key
 * @param {?=} defaultValue
 * @return {?}
 */
function LocalStorage(key, defaultValue) {
    return function (prototype, propName) {
        DecoratorBuilder.buildSyncStrategyDecorator(StorageStrategies.Local, prototype, propName, key, defaultValue);
    };
}
/**
 * @param {?=} key
 * @param {?=} defaultValue
 * @return {?}
 */
function SessionStorage(key, defaultValue) {
    return function (prototype, propName) {
        DecoratorBuilder.buildSyncStrategyDecorator(StorageStrategies.Session, prototype, propName, key, defaultValue);
    };
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/** @type {?} */
var Services = [
    LocalStorageServiceProvider,
    SessionStorageServiceProvider
];

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/** @type {?} */
var LIB_CONFIG = new InjectionToken('ngx_webstorage_config');
/**
 * @param {?} index
 * @return {?}
 */
function appInit(index) {
    index.indexStrategies();
    return function () { return StrategyIndex.index; };
}
var NgxWebstorageModule = /** @class */ (function () {
    function NgxWebstorageModule(index, config) {
        if (config)
            StorageKeyManager.consumeConfiguration(config);
        else
            console.error('NgxWebstorage : Possible misconfiguration (The forRoot method usage is mandatory since the 3.0.0)');
    }
    /**
     * @param {?=} config
     * @return {?}
     */
    NgxWebstorageModule.forRoot = /**
     * @param {?=} config
     * @return {?}
     */
    function (config) {
        if (config === void 0) { config = {}; }
        return {
            ngModule: NgxWebstorageModule,
            providers: __spread([
                {
                    provide: LIB_CONFIG,
                    useValue: config,
                },
                LocalStorageProvider,
                SessionStorageProvider
            ], Services, Strategies, [
                {
                    provide: APP_INITIALIZER,
                    useFactory: appInit,
                    deps: [StrategyIndex],
                    multi: true
                }
            ])
        };
    };
    NgxWebstorageModule.decorators = [
        { type: NgModule, args: [{},] }
    ];
    /** @nocollapse */
    NgxWebstorageModule.ctorParameters = function () { return [
        { type: StrategyIndex },
        { type: undefined, decorators: [{ type: Optional }, { type: Inject, args: [LIB_CONFIG,] }] }
    ]; };
    return NgxWebstorageModule;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

export { LOCAL_STORAGE, SESSION_STORAGE, STORAGE_STRATEGIES, LocalStorageService, SessionStorageService, StorageStrategies, SyncStorage, AsyncStorage, StrategyCacheService, LocalStorageStrategy, SessionStorageStrategy, InMemoryStorageStrategy, StorageStrategyStubName, StorageStrategyStub, StorageStub, InvalidStrategyError, StrategyIndex, LocalStorage, SessionStorage, appInit, LIB_CONFIG, NgxWebstorageModule, LocalStorageProvider as ɵb, SessionStorageProvider as ɵd, getLocalStorage as ɵa, getSessionStorage as ɵc, Services as ɵn, LocalStorageServiceProvider as ɵg, buildService as ɵf, SessionStorageServiceProvider as ɵi, buildService$1 as ɵh, STORAGE_STRATEGIES as ɵl, BaseSyncStorageStrategy as ɵj, Strategies as ɵe };

//# sourceMappingURL=data:application/json;charset=utf-8;base64,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